import axios from "axios";
import { connection_string } from "./connection_strings";
import { getAccessToken, removeAccessToken, setAccessToken } from "./authHeader";
import { Error } from "../Components/Modal";

const api = axios.create({
    baseURL: connection_string,
});

api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers["Authorization"] = "Bearer " + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Sent by the backend role guard: the token is valid but does not belong to an admin.
const NOT_ADMIN_MESSAGE = "You are not authorized to use make this request.";

const decodeTokenPayload = (token) => {
    try {
        const part = String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
    } catch (e) {
        return null;
    }
};

// A renewal keeps the same user and session id (old tokens without sid: same user); anything else is a stale header.
const isSameSession = (current, renewed) => {
    const pc = decodeTokenPayload(current);
    const pr = decodeTokenPayload(renewed);
    if (!pc?.userId || pc.userId !== pr?.userId) return false;
    return !pc.sid || pc.sid === pr.sid;
};

// Sliding login expiry: the backend sends a renewed token in X-Auth-Token while the admin keeps using the panel.
const adoptRenewedToken = (response) => {
    const renewed = response?.headers?.["x-auth-token"];
    if (!renewed) return;
    const current = getAccessToken();
    const sentWith = String(response.config?.headers?.Authorization || "").replace(/^Bearer /, "");
    // Logged out or logged in again while this request was in flight.
    if (!current || current !== sentWith) return;
    if (renewed === current || !isSameSession(current, renewed)) return;
    setAccessToken(renewed);
    window.dispatchEvent(new Event("muvrrs:token-renewed"));
};

api.interceptors.response.use(
    function (response) {
        adoptRenewedToken(response);
        return response;
    },
    function (error) {
        const status = error.response?.status;
        if (status !== 401) adoptRenewedToken(error.response);
        const message = error.response?.data?.message;
        if (status === 401) {
            if (message) {
                sessionStorage.setItem("logoutToast", message);
            }
            removeAccessToken();
            window.location.href = "/superadmin";
        } else if (status === 403 && message === NOT_ADMIN_MESSAGE) {
            sessionStorage.setItem("logoutToast", "This account does not have admin access.");
            removeAccessToken();
            window.location.href = "/superadmin";
        }
        return Promise.reject(error);
    }
);

export default api;
